// Inbound triage — bulk action toolbar (Fig59 / image23): Invite / Export /
// Tag / Clear. Appears once at least one row is selected. Selection is
// intentionally NOT scoped to the currently-visible (filtered) rows — the
// parent page keeps every checked id regardless of filter state, and this bar
// always acts on the FULL selection. The banner above the toolbar makes that
// explicit and honest: it names exactly which selected applicants the active
// filter is currently hiding, computed from real data — never a fabricated
// count.
import { useState } from 'react'
import { Send, Download, Tag as TagIcon } from 'lucide-react'
import { getUserPreference, setUserPreference } from '../../services/api'
import type { AggregatorProject } from '../../types'
import BulkInviteDialog from './BulkInviteDialog'
import { matchStatusMeta } from './InboundTriageTable'

interface InboundBulkActionBarProps {
  /** every selected project id, including ones hidden by the current filter */
  selectedProjectIds: string[]
  /** the real project records behind selectedProjectIds (unfiltered) */
  selectedProjects: AggregatorProject[]
  /** subset of selectedProjects currently hidden by the active filter */
  hiddenProjects: AggregatorProject[]
  activeAggregatorId: string | null
  onClear: () => void
  onComplete: () => void
}

type Toast = { message: string; type: 'success' | 'error' }

export default function InboundBulkActionBar({
  selectedProjectIds,
  selectedProjects,
  hiddenProjects,
  activeAggregatorId,
  onClear,
  onComplete,
}: InboundBulkActionBarProps) {
  const [tagMode, setTagMode] = useState(false)
  const [tagText, setTagText] = useState('')
  const [loading, setLoading] = useState<string | null>(null)
  const [toast, setToast] = useState<Toast | null>(null)
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  const showToast = (message: string, type: Toast['type'] = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const exportCSV = () => {
    const headers = ['Applicant', 'Matched venture', 'Sector', 'Match score', 'Received', 'Status']
    const rows = selectedProjects.map((p) => [
      p.entity_name ?? '',
      p.name ?? '',
      p.vertical ?? '',
      p.top_match_score != null ? `${Math.round(p.top_match_score * 100)}%` : '',
      p.created_at ? new Date(p.created_at).toLocaleDateString('en-AU') : '',
      matchStatusMeta(p.match_run_status).label,
    ])
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inbound-triage-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleSaveTag = async () => {
    if (!tagText.trim() || !activeAggregatorId) return
    setLoading('tag')
    try {
      // Reuses the SAME preference key as the portfolio's BulkActionBar so a
      // tag applied here shows up everywhere the project is tagged — tags
      // belong to the project, not to whichever screen set them.
      const key = `aggregator_project_tags:${activeAggregatorId}`
      const existing = await getUserPreference(key).catch(() => null)
      const currentTags: Record<string, string[]> = (existing?.value as Record<string, string[]>) ?? {}
      const merged = { ...currentTags }
      for (const pid of selectedProjectIds) {
        merged[pid] = [...new Set([...(merged[pid] ?? []), tagText.trim()])]
      }
      await setUserPreference(key, merged)
      showToast(`Tagged ${selectedProjectIds.length} applicant${selectedProjectIds.length === 1 ? '' : 's'} with '${tagText.trim()}'`)
      setTagMode(false)
      setTagText('')
      onComplete()
    } catch {
      showToast('Failed to save tags', 'error')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="mb-4" data-testid="inbound-bulk-wrapper">
      <div
        className="mb-2 rounded-lg border border-iflow-200 bg-iflow-50 px-4 py-2.5 text-sm leading-relaxed text-iflow-900"
        data-testid="inbound-selection-banner"
      >
        <span className="font-semibold">{selectedProjectIds.length} selected</span> — acting on all{' '}
        {selectedProjectIds.length} across the whole inbound queue, not just the current filter.
        {hiddenProjects.length > 0 && (
          <span data-testid="inbound-hidden-banner">
            {' '}
            <span className="font-semibold">{hiddenProjects.length} hidden</span> by the active filter:{' '}
            {hiddenProjects.map((p) => p.entity_name).join(', ')}. Clear the filter to see them, or run the bulk
            action now — it applies to all {selectedProjectIds.length} either way.
          </span>
        )}
      </div>

      <div
        data-testid="inbound-bulk-action-bar"
        className="flex flex-wrap items-center gap-3 rounded-xl bg-iflow-900 px-4 py-3 text-white shadow-lg"
      >
        <span className="text-sm font-semibold" data-testid="inbound-selection-count">
          {selectedProjectIds.length} selected
        </span>

        <button
          data-testid="inbound-bulk-invite"
          onClick={() => setShowInviteDialog(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium hover:bg-white/20"
        >
          <Send size={14} /> Invite
        </button>

        <button
          data-testid="inbound-bulk-export"
          onClick={exportCSV}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium hover:bg-white/20"
        >
          <Download size={14} /> Export CSV
        </button>

        {tagMode ? (
          <div className="flex items-center gap-2" data-testid="inbound-tag-input-row">
            <input
              data-testid="inbound-tag-input"
              type="text"
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              placeholder="Tag name..."
              className="rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTag()
              }}
            />
            <button
              data-testid="inbound-tag-save"
              onClick={handleSaveTag}
              disabled={loading === 'tag' || !tagText.trim()}
              className="rounded-lg bg-amber-400 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-amber-300 disabled:opacity-50"
            >
              {loading === 'tag' ? 'Saving…' : 'Save'}
            </button>
            <button
              data-testid="inbound-tag-cancel"
              onClick={() => {
                setTagMode(false)
                setTagText('')
              }}
              className="text-sm text-white/70 hover:text-white"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid="inbound-bulk-tag"
            onClick={() => setTagMode(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium hover:bg-white/20"
          >
            <TagIcon size={14} /> Tag
          </button>
        )}

        <button
          data-testid="inbound-bulk-clear"
          onClick={onClear}
          className="ml-auto text-sm font-medium text-white/80 hover:text-white hover:underline"
        >
          Clear selection
        </button>

        {toast && (
          <div
            data-testid="inbound-bulk-toast"
            className={`w-full text-sm font-medium sm:w-auto ${toast.type === 'error' ? 'text-rose-200' : 'text-emerald-200'}`}
          >
            {toast.message}
          </div>
        )}
      </div>

      {showInviteDialog && (
        <BulkInviteDialog
          selectedProjectIds={selectedProjectIds}
          projects={selectedProjects}
          activeAggregatorId={activeAggregatorId}
          onClose={() => {
            setShowInviteDialog(false)
            onComplete()
          }}
        />
      )}
    </div>
  )
}
